import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const offices = [
  {
    type: 'Head Office',
    city: 'Coimbatore',
    address: 'SF NO: 328/1D1, DNO: 365F, Alampalayam Pirivu, Thennampalayam, Annur Road, Coimbatore – 641107, Tamilnadu, India',
    emails: ['info@sevagases.com', 'ceo@sevagases.com'],
    phones: ['+91 9894020509'],
  },
];

const subjects = ['General Enquiry', 'Gases & Liquids', 'Air Separation Plants', 'Maintenance Services', 'Other'];

const initialForm = { name: '', company: '', phone: '', email: '', subject: 'General Enquiry', message: '' };

export default function ContactPage() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.company.trim()) e.company = 'Company name is required';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email is required';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(initialForm);
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  return (
    <div className="pt-20">
      {/* HERO */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-red mb-4 block">Get in Touch</span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tighter font-heading mb-6">Contact Us</h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Our experts are ready to answer any questions about our products, plants, and engineering services. Reach out today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* FORM */}
            <div className="lg:col-span-2">
              <motion.div {...fadeUp}>
                <span className="section-tag mb-3 block">Send a Message</span>
                <h2 className="text-3xl font-bold text-gray-900 font-heading mb-8">Fill Out the Form Below</h2>

                {status === 'success' ? (
                  <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="contact-success">
                    <CheckCircle size={40} className="text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 font-heading mb-2">Message Sent Successfully!</h3>
                    <p className="text-gray-600">We will definitely get back to you within 24–48 hours. Thank you for your patience.</p>
                    <button onClick={() => setStatus('')} className="mt-6 btn-primary">Send Another Message</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                        <input name="name" value={form.name} onChange={handleChange} placeholder="Your full name"
                          data-testid="contact-name-input"
                          className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red ${errors.name ? 'border-red-400' : 'border-gray-200'}`} />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX"
                          data-testid="contact-phone-input"
                          className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red ${errors.phone ? 'border-red-400' : 'border-gray-200'}`} />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                      <input name="company" value={form.company} onChange={handleChange} placeholder="Your company or organisation name"
                        data-testid="contact-company-input"
                        className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red ${errors.company ? 'border-red-400' : 'border-gray-200'}`} />
                      {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <input name="email" value={form.email} onChange={handleChange} placeholder="you@company.com" type="email"
                        data-testid="contact-email-input"
                        className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red ${errors.email ? 'border-red-400' : 'border-gray-200'}`} />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Subject *</label>
                      <select name="subject" value={form.subject} onChange={handleChange}
                        data-testid="contact-subject-select"
                        className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red bg-white">
                        {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Message *</label>
                      <textarea name="message" value={form.message} onChange={handleChange} rows={5}
                        placeholder="Tell us about your gas requirements, volume needs, or any specific questions..."
                        data-testid="contact-message-input"
                        className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red resize-none ${errors.message ? 'border-red-400' : 'border-gray-200'}`} />
                      {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                    </div>
                    {status === 'error' && (
                      <p className="text-red-500 text-sm bg-red-50 border border-red-200 px-4 py-3" data-testid="contact-error">
                        Something went wrong. Please try again or call us directly.
                      </p>
                    )}
                    <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>

            {/* OFFICES */}
            <div className="space-y-6">
              {offices.map((office, i) => (
                <motion.div key={office.city} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="border border-gray-200 p-7 card-hover" data-testid={`office-${office.city.toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-brand-red text-white text-xs px-2.5 py-1 font-semibold">{office.type}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-heading mb-4">{office.city}</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="text-brand-red mt-0.5 flex-shrink-0" />
                      <p className="text-gray-600 text-sm leading-relaxed">{office.address}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone size={16} className="text-brand-red mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        {office.phones.map(ph => (
                          <a key={ph} href={`tel:${ph}`} className="block text-gray-600 text-sm hover:text-brand-red transition-colors">{ph}</a>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail size={16} className="text-brand-red mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        {office.emails.map(em => (
                          <a key={em} href={`mailto:${em}`} className="block text-gray-600 text-sm hover:text-brand-red transition-colors">{em}</a>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              <motion.div {...fadeUp} className="bg-gray-50 border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock size={16} className="text-brand-red" />
                  <h4 className="font-bold text-gray-900 font-heading text-sm">Business Hours</h4>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>Monday – Saturday: <span className="font-medium">9:00 AM – 6:00 PM</span></p>
                  <p>Sunday: <span className="font-medium text-gray-400">Closed</span></p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP — OpenStreetMap embed, pinned to Coimbatore HQ */}
      <section className="h-96 bg-gray-200 relative overflow-hidden">
        <iframe
          title="Seva Gases Coimbatore Office"
          src="https://www.openstreetmap.org/export/embed.html?bbox=76.8958%2C11.0568%2C77.0158%2C11.1368&layer=mapnik&marker=11.0968%2C76.9558"
          style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
          allowFullScreen
          loading="lazy"
          data-testid="office-map"
        />
        {/* Floating label */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="bg-white px-6 py-3 shadow-lg border-t-4 border-[#CC2222] flex items-center gap-3 whitespace-nowrap">
            <MapPin size={18} className="text-[#CC2222] flex-shrink-0" />
            <div>
              <p className="font-bold text-[#111111] text-sm font-heading leading-none">Coimbatore, Tamil Nadu</p>
              <p className="text-gray-500 text-xs mt-0.5">Head Office · Annur Road</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
